/**
 * Aday olay havuzu ve gelen aday uçları (v3.1 Dalga 2A dilim S9 — D2 + D7). Tasarım: docs/planning/v3/lead-dagitim-ve-kaynak-
 * izleme.md § 4; belge docs/spec/castinflow/lead-intake.md.
 *
 * - Her kaynaktan gelen başvuru önce bir OLAY olarak havuza düşer (`lead_events`): kaynak bağlayıcı, dış olay kimliği, tekil
 *   tekrar anahtarı, durum (`received → processing → processed | rejected | failed → dead`), deneme sayısı, hata kodu, ŞİFRELİ ve
 *   SÜRELİ ham yük, maskeli görünüm, oluşan/bağlanan aday ve karar.
 * - Aynı olay ikinci kez gelirse yeni satır ve yeni aday oluşmaz: yanıt `duplicate` olur, ilk olayın tekrar sayacı artar.
 * - Uç başına anahtar YALNIZ `leads:ingest` kapsamındadır (başka hiçbir uçta geçmez). Genel web kancası HMAC-SHA256 imzası
 *   (`X-Castinflow-Signature: t=<unix>,v1=<onaltılık>`, 5 dk tolerans) ister; reklam platformunun potansiyel müşteri formu
 *   uçları form başına anahtarı (`google_key`) gövdede doğrular. İki anahtar türü de çift anahtarlı rotasyonla döner.
 * - Ham yükü görmek ayrı yetenektir (`lead_events.view_raw`) ve her görüntüleme denetime yazılır; yeniden işleme
 *   `lead_events.manage` ister.
 */
import { z } from 'zod';
import { TOUCH_CLICK_ID_KINDS } from './acquisition.js';
import { CONSENT_CHANNELS } from './conversations.js';
const uuid = z.guid();
// ---------------------------------------------------------------------------------------------------------------------
// Uçlar
// ---------------------------------------------------------------------------------------------------------------------
export const LEAD_ENDPOINT_KINDS = ['webhook', 'google_lead_form'];
export const LEAD_ENDPOINT_KIND_LABELS = {
    webhook: 'Genel web kancası (imzalı)',
    google_lead_form: 'Google Ads potansiyel müşteri formu',
};
/**
 * Olayın bağlayıcı türü (`lead_events.connector` öneki): gelen aday uçları (`webhook`, `google_lead_form`), sağlayıcı
 * bağlantısı üzerinden gelen sosyal medya sayfası potansiyel müşteri formları (`meta_lead_ads`; v3.1 S13 — D5′) ve iş
 * mesajlaşma hattına reklam/kampanya kaynaklı gelen ilk ileti (`whatsapp_ctwa`; v3.1 S14 — E7; başvuru sağlayıcıdan OKUNMAZ,
 * konuşmadan gelir).
 */
export const LEAD_CONNECTOR_KINDS = [
    'webhook',
    'google_lead_form',
    'meta_lead_ads',
    'whatsapp_ctwa',
];
/** Bağlayıcının sağlayıcı bağlantısıyla mı geldiği (başvuru sağlayıcıdan ayrıca OKUNUR) */
export function isProviderLeadConnector(kind) {
    return kind === 'meta_lead_ads';
}
/** `connector` dizgisinden tür (`<tür>:<kimlik>`); tanınmıyorsa `webhook` değil `null` */
export function leadConnectorKindOf(connector) {
    const kind = connector.split(':')[0] ?? '';
    return LEAD_CONNECTOR_KINDS.includes(kind)
        ? kind
        : null;
}
/** Uç anahtarının tek kapsamı */
export const LEAD_INGEST_SCOPE = 'leads:ingest';
export const LEAD_HOOK_LIMITS = {
    /** İstek gövdesi üst sınırı (bayt) — aşan istek 413 */
    maxBodyBytes: 65_536,
    /** İmza zaman toleransı (saniye) */
    signatureToleranceSeconds: 300,
    /** Uç başına dakikalık istek sınırı */
    endpointPerMinute: 120,
    /** Ham yük saklama süresi (gün); sonra yük yok edilir, maskeli görünüm ve üst veri kalır */
    rawRetentionDays: 30,
    /** Otomatik deneme sayısı; aşılınca olay `dead` */
    maxAttempts: 5,
    /** Çalışma alanı başına uç sayısı */
    maxEndpoints: 20,
};
export const LEAD_SIGNATURE_HEADER = 'x-castinflow-signature';
export const leadEndpointViewSchema = z.object({
    id: uuid,
    kind: z.enum(LEAD_ENDPOINT_KINDS),
    name: z.string(),
    scope: z.literal(LEAD_INGEST_SCOPE),
    isActive: z.boolean(),
    sourceKey: z.string(),
    defaultOwnerId: uuid.nullable(),
    defaultOwnerName: z.string().nullable(),
    /** Gönderim adresi (genel kimlik adreste; sır adreste değildir) */
    url: z.string(),
    secretVersion: z.number().int(),
    secretRotatedAt: z.string().nullable(),
    /** Rotasyon geçişi sürüyorsa önceki anahtar (değer değil): sürüm + bitiş */
    previousSecret: z.object({ version: z.number().int(), expiresAt: z.string() }).nullable(),
    lastEventAt: z.string().nullable(),
    createdAt: z.string(),
    /** Son 7 günün sayaçları (sebep → adet; imzasız istekler dahil, olay kaydı olmadan sayılır) */
    metrics: z.record(z.string(), z.number().int()),
});
export const leadEndpointListSchema = z.object({ data: z.array(leadEndpointViewSchema) });
/** Oluşturma ve döndürme yanıtı: sır YALNIZ bu yanıtta bir kez görünür */
export const leadEndpointSecretResponseSchema = z.object({
    endpoint: leadEndpointViewSchema,
    secret: z.string(),
});
const sourceKey = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/, 'geçersiz kaynak');
export const createLeadEndpointInputSchema = z
    .object({
    kind: z.enum(LEAD_ENDPOINT_KINDS),
    name: z.string().trim().min(1, 'ad gerekli').max(120),
    sourceKey,
    defaultOwnerId: uuid.nullable().optional(),
    isActive: z.boolean().default(true),
})
    .strict();
export const updateLeadEndpointInputSchema = z
    .object({
    name: z.string().trim().min(1, 'ad gerekli').max(120).optional(),
    sourceKey: sourceKey.optional(),
    defaultOwnerId: uuid.nullable().optional(),
    isActive: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Değişiklik yok' });
export const rotateLeadEndpointInputSchema = z
    .object({ overlapHours: z.number().int().min(0).max(168).default(24) })
    .strict();
// ---------------------------------------------------------------------------------------------------------------------
// Olaylar
// ---------------------------------------------------------------------------------------------------------------------
export const LEAD_EVENT_STATUSES = [
    'received',
    'processing',
    'processed',
    'duplicate',
    'rejected',
    'failed',
    'dead',
];
export const LEAD_EVENT_STATUS_LABELS = {
    received: 'Alındı',
    processing: 'İşleniyor',
    processed: 'İşlendi',
    duplicate: 'Tekrar',
    rejected: 'Reddedildi',
    failed: 'Başarısız (yeniden denenecek)',
    dead: 'Kalıcı başarısız',
};
/** Yeniden işlenebilen durumlar (`processed` ve `rejected` yeniden işlenmez) */
export const LEAD_EVENT_REPROCESSABLE = ['failed', 'dead'];
export const LEAD_EVENT_DECISIONS = ['created', 'attached', 'created_flagged', 'test'];
export const LEAD_EVENT_DECISION_LABELS = {
    created: 'Yeni aday oluşturuldu',
    attached: 'Mevcut açık adaya temas eklendi (tekrar başvurdu)',
    created_flagged: 'Yeni aday oluşturuldu — olası mükerrer, eşleştirme kuyruğunda',
    test: 'Deneme olayı (aday oluşturulmadı)',
};
export const LEAD_EVENT_ERROR_CODES = [
    'schema_invalid',
    'plan_limit',
    'lead_requirements',
    'payload_purged',
    'internal',
    // v3.1 S13: başvurunun sağlayıcıdan okunması (sağlayıcı bağlantılı bağlayıcılar)
    'connection_blocked',
    'remote_temporary',
    'remote_rejected',
];
export const LEAD_EVENT_ERROR_LABELS = {
    schema_invalid: 'Yük biçimi geçersiz (gönderen düzeltmeli)',
    plan_limit: 'Aylık aday olayı tavanı doldu',
    lead_requirements: 'Zorunlu aday alanları bu kaynaktan karşılanamıyor',
    payload_purged: 'Ham yük saklama süresi dolduğu için silindi',
    internal: 'Beklenmeyen hata',
    connection_blocked: 'Sağlayıcı bağlantısı kullanılamıyor (yetki iptali, hata ya da bağlantı kesildi); bağlantı düzelince yeniden denenir',
    remote_temporary: 'Başvuru sağlayıcıdan şimdi okunamadı (geçici hata); yeniden denenecek',
    remote_rejected: 'Sağlayıcı başvuruyu vermedi (silinmiş ya da erişim izni yok)',
};
/** Maskeli görünüm: kişisel veri maskelenir; bağlantı etiketleri gösterilir; tıklama kimliklerinin yalnız türü */
export const leadEventMaskedSchema = z.object({
    name: z.string().nullable(),
    company: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    messageLength: z.number().int().nullable(),
    utm: z.record(z.string(), z.string()),
    clickIdKinds: z.array(z.enum(TOUCH_CLICK_ID_KINDS)),
    landingPath: z.string().nullable(),
    referrerHost: z.string().nullable(),
    /** Platform alanları (form, kampanya, grup, reklam kimliği) */
    platform: z.record(z.string(), z.string()),
    /** Reddedilen olayda yük doğrulama hataları (alan yolu + ileti; değer yok) */
    errors: z.array(z.object({ path: z.string(), message: z.string() })),
});
export const leadEventViewSchema = z.object({
    id: uuid,
    connector: z.string(),
    connectorKind: z.enum(LEAD_CONNECTOR_KINDS),
    /** Sağlayıcı bağlantılı bağlayıcıda bağlantı (uçlu bağlayıcıda null) ve görünen adı */
    providerConnectionId: uuid.nullable(),
    providerConnectionName: z.string().nullable(),
    endpointId: uuid.nullable(),
    endpointName: z.string().nullable(),
    endpointKind: z.enum(LEAD_ENDPOINT_KINDS).nullable(),
    externalEventId: z.string(),
    isTest: z.boolean(),
    receivedAt: z.string(),
    occurredAt: z.string().nullable(),
    status: z.enum(LEAD_EVENT_STATUSES),
    attempt: z.number().int(),
    nextAttemptAt: z.string().nullable(),
    lastErrorCode: z.enum(LEAD_EVENT_ERROR_CODES).nullable(),
    decision: z.enum(LEAD_EVENT_DECISIONS).nullable(),
    /** Oluşan ya da bağlanan aday (çağıran göremiyorsa başlık null) */
    lead: z.object({ id: uuid, title: z.string().nullable() }).nullable(),
    duplicateCount: z.number().int(),
    rawAvailable: z.boolean(),
    purgeRawAfter: z.string(),
    processedAt: z.string().nullable(),
    masked: leadEventMaskedSchema,
});
/** Liste süzgeçleri: durumlar + "tekrar gelenler" */
export const LEAD_EVENT_FILTERS = [
    'all',
    'open',
    'processed',
    'rejected',
    'failed',
    'dead',
    'duplicates',
];
export const LEAD_EVENT_FILTER_LABELS = {
    all: 'Tümü',
    open: 'Alındı / işleniyor',
    processed: 'İşlendi',
    rejected: 'Reddedildi',
    failed: 'Başarısız',
    dead: 'Kalıcı başarısız',
    duplicates: 'Tekrar gelenler',
};
export const leadEventListQuerySchema = z
    .object({
    filter: z.enum(LEAD_EVENT_FILTERS).default('all'),
    endpointId: uuid.optional(),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
})
    .strict();
export const leadEventListSchema = z.object({
    data: z.array(leadEventViewSchema),
    nextCursor: z.string().nullable(),
    counts: z.partialRecord(z.enum(LEAD_EVENT_STATUSES), z.number().int()),
});
export const reprocessLeadEventsInputSchema = z
    .object({ ids: z.array(uuid).min(1).max(50) })
    .strict();
export const reprocessResultSchema = z.object({
    results: z.array(z.object({
        id: uuid,
        outcome: z.enum(['processed', 'failed', 'dead', 'skipped', 'not_found']),
        status: z.enum(LEAD_EVENT_STATUSES).nullable(),
    })),
});
export const leadEventRawSchema = z.object({
    id: uuid,
    /** Ham yük (gönderildiği hâliyle, JSON) — yalnız `lead_events.view_raw`, her görüntüleme denetime yazılır */
    payload: z.unknown(),
});
// ---------------------------------------------------------------------------------------------------------------------
// Gelen yükler
// ---------------------------------------------------------------------------------------------------------------------
const opt = (max) => z.string().trim().min(1).max(max).optional();
const EMAIL_LOOSE = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/;
/**
 * Toplama dayanağı ve kanal başına iletişim uygunluğu (EK4; `docs/planning/ekosistem/mimari-ve-sozlesmeler.md` § 7.1). Gönderen
 * uygulamanın (IQ7S ve benzeri, Castcall) BEYANIDIR: Castinflow bunu izin defterine `api` kaynaklı, kanıt metniyle yazar.
 *  - `consented`  → kanal × pazarlama için "izin verildi" satırı
 *  - `prohibited` → kanal × pazarlama için "izin geri çekildi" satırı (hizmet iletişimi izinden bağımsızdır; § consent.ts)
 *  - `service_only`, `unknown` → satır YAZILMAZ (izin yokluğu zaten pazarlama gönderimini engeller)
 * `public_source` ve `purchased_list` dayanağı ile `unknown` uygunluk iletişim izni SAYILMAZ; kayıt CRM'e alınır, otomatik temas
 * yapılmaz (aday kaynaklı otomatik gönderim zaten yoktur: sekans e-posta adımı insan onayıyla gider; ajan ilk teması kurmaz).
 */
export const LEAD_COLLECTION_BASES = [
    'form_submission',
    'inbound_contact',
    'referral',
    'public_source',
    'purchased_list',
    'manual',
];
export const LEAD_COLLECTION_BASIS_LABELS = {
    form_submission: 'Form başvurusu',
    inbound_contact: 'Kişinin kendi teması',
    referral: 'Tavsiye',
    public_source: 'Herkese açık kaynak',
    purchased_list: 'Satın alınmış liste',
    manual: 'Elle girildi',
};
export const LEAD_CONTACTABILITY = ['consented', 'service_only', 'unknown', 'prohibited'];
export const LEAD_CONTACTABILITY_LABELS = {
    consented: 'Pazarlama izni var',
    service_only: 'Yalnız hizmet iletişimi',
    unknown: 'Bilinmiyor',
    prohibited: 'İletişim istemiyor',
};
export const leadCollectionSchema = z
    .object({
    basis: z.enum(LEAD_COLLECTION_BASES),
    /** Kanal başına uygunluk (verilmeyen kanal `unknown`) */
    contactability: z
        .object({
        email: z.enum(LEAD_CONTACTABILITY).optional(),
        whatsapp: z.enum(LEAD_CONTACTABILITY).optional(),
        sms: z.enum(LEAD_CONTACTABILITY).optional(),
        phone: z.enum(LEAD_CONTACTABILITY).optional(),
    })
        .strict()
        .prefault({}),
    /** Kanıt: izin metni sürümü, form adı, arama kaydı kimliği … (izin defterine yazılır) */
    evidence: z.string().trim().min(3, 'kanıt en az 3 karakter olmalı').max(500),
    /** Beyanın alındığı an (verilmezse olay anı; gelecek olamaz — işlemede denetlenir) */
    capturedAt: z.iso.datetime({ offset: true }).optional(),
})
    .strict();
/** Uygunluk → izin defteri satırı (saf). Yalnız `consented` ve `prohibited` satır üretir. */
export function consentRowsFromCollection(c) {
    const out = [];
    for (const channel of CONSENT_CHANNELS) {
        const v = c.contactability[channel];
        if (v === 'consented')
            out.push({ channel, action: 'grant' });
        else if (v === 'prohibited')
            out.push({ channel, action: 'withdraw' });
    }
    return out;
}
/** Genel web kancası yükü (katı şema: bilinmeyen alan → 422 `rejected`) */
export const leadWebhookPayloadSchema = z
    .object({
    /** Gönderenin olay kimliği — tekrar anahtarı bundan türetilir */
    eventId: z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/, 'geçersiz olay kimliği'),
    occurredAt: z.iso.datetime({ offset: true }).optional(),
    /** Deneme olayı: kaydedilir, aday oluşturulmaz */
    test: z.boolean().optional(),
    lead: z
        .object({
        firstName: opt(100),
        lastName: opt(100),
        fullName: opt(200),
        companyName: opt(200),
        title: opt(120),
        email: z.string().trim().max(254).regex(EMAIL_LOOSE, 'geçersiz e-posta').optional(),
        phone: z
            .string()
            .trim()
            .max(40)
            .regex(/^[+0-9 ()./-]{5,40}$/, 'geçersiz telefon')
            .optional(),
        message: z.string().max(10_000).optional(),
    })
        .strict()
        .refine((l) => l.firstName !== undefined ||
        l.lastName !== undefined ||
        l.fullName !== undefined ||
        l.email !== undefined ||
        l.phone !== undefined, { message: 'ad, e-posta ya da telefondan en az biri gerekli' }),
    attribution: z
        .object({
        utmSource: opt(500),
        utmMedium: opt(500),
        utmCampaign: opt(500),
        utmTerm: opt(500),
        utmContent: opt(500),
        gclid: opt(500),
        gbraid: opt(500),
        wbraid: opt(500),
        fbclid: opt(500),
        msclkid: opt(500),
        ttclid: opt(500),
        landingUrl: opt(2000),
        referrer: opt(2000),
        /** Gönderenin bildirdiği kampanya (yalnız VAR OLAN kampanyaya bağlanır; kesinlik `associated`) */
        campaign: z
            .object({
            platform: z.enum(['meta', 'google', 'other']),
            externalId: z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/, 'geçersiz kampanya kimliği'),
        })
            .strict()
            .optional(),
    })
        .strict()
        .optional(),
    /** EK4: toplama dayanağı + kanal başına iletişim uygunluğu (gönderenin beyanı; izin defterine yazılır) */
    collection: leadCollectionSchema.optional(),
})
    .strict();
/** 8 baytlık tam sayı alanı: sayı ya da rakam dizgisi olarak gelebilir → dizgi */
const int64 = z
    .union([z.number().int().nonnegative(), z.string().regex(/^\d{1,20}$/)])
    .transform((v) => String(v));
/**
 * Reklam platformu potansiyel müşteri formu yükü (developers.google.com/google-ads/webhook/docs/implementation). Platform yeni
 * alan ekleyebilir: bilinmeyen alanlar ATILIR (reddedilmez — 4XX yanıt yeniden denenmez, başvuru kaybolur).
 */
export const googleLeadFormPayloadSchema = z.object({
    lead_id: z.string().min(1).max(200),
    user_column_data: z
        .array(z.object({
        column_id: z.string().max(100).optional(),
        string_value: z.string().max(5000).optional(),
        column_name: z.string().max(200).optional(),
    }))
        .max(200)
        .default([]),
    api_version: z.string().max(20).optional(),
    form_id: int64.optional(),
    campaign_id: int64.optional(),
    google_key: z.string().min(1).max(200),
    is_test: z.boolean().optional(),
    gcl_id: z.string().max(500).optional(),
    /** Yalnız video ve keşif reklamları */
    adgroup_id: int64.optional(),
    creative_id: int64.optional(),
    /** Yalnız en yüksek performans kampanyaları */
    asset_group_id: int64.optional(),
    lead_submit_time: z.string().max(64).optional(),
    lead_stage: z.string().max(64).optional(),
    lead_source: z.string().max(64).optional(),
});
/** Saklanan biçim: form anahtarı (`google_key`) ham yükten ÇIKARILIR (sır saklanmaz) */
export const googleLeadFormStoredSchema = googleLeadFormPayloadSchema.omit({ google_key: true });
/** Gelen web kancası yanıtı (202 kabul / 200 tekrar) */
export const leadHookResultSchema = z.object({
    status: z.enum(['accepted', 'duplicate']),
    eventId: uuid,
});
//# sourceMappingURL=lead-events.js.map